import { zodResolver } from '@hookform/resolvers/zod';
import { ApiError, TicketsController_readByNumber_v1 } from '@repo/api-client';
import { Button } from '@repo/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle, IconSearch, IconTicketOff } from '@tabler/icons-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { isNotFound, messageForFailure } from '@/api/failure';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import { ticketPath } from '../paths';
import {
  TICKET_NUMBER_EXAMPLE,
  TICKET_NUMBER_MALFORMED,
  ticketNumberSchema,
} from './ticket-number';

/**
 * The number off a visitor's ticket, and the record it belongs to.
 *
 * ## This is the dispute path, and it is the point of the screen it sits on
 *
 * A visitor comes back to the counter with a printed ticket and a question. The
 * number on that artefact is all anybody has — not an id, not a day, not which
 * gate — so this is a single field that takes what is in their hand and answers
 * with the record. It is put where a manager already is, at the top of the
 * tickets screen and again on a ticket's own record, rather than behind a search
 * page somebody has to navigate to first: the target is under thirty seconds
 * from the number in somebody's hand to the record on screen, and a navigation
 * step spends a good part of that before anything has been typed.
 *
 * ## Found, and then a page rather than a panel
 *
 * The answer is a navigation to that ticket's own address and not an expanding
 * panel here, which costs a second request (`by-number` resolves, `{id}` reads)
 * and buys three things a panel cannot: the record survives a refresh, it can be
 * kept open in a second tab beside a queue, and its URL can be handed to
 * somebody else. A dispute is a conversation, and a screen state that evaporates
 * on reload is a poor place to hold one.
 *
 * ## What "not found" is allowed to say
 *
 * **A number at an outlet outside the caller's reach answers `404` byte for byte
 * identically to one nobody has printed** (FR-128), deliberately, because a
 * refusal that told them apart is an enumeration oracle — the same rule as
 * `AUTH_CREDENTIALS_INVALID` covering an unknown staff code, a wrong password
 * and a deactivated user, and `docs/ui-conventions.md` names it under "Preserve
 * the non-distinctions". So the copy below says *no ticket with that number* and
 * nothing more. It does not hedge, it does not offer "you may not have access to
 * it", and there is no branch here that could tell the two apart if somebody
 * asked it to.
 *
 * What it does say is that a **voided** ticket would have been found — because
 * the most dangerous misreading of an empty answer, on this screen of all
 * screens, is "the ticket must have been voided".
 */

const lookupSchema = z.object({ number: ticketNumberSchema });

/**
 * Every state the panel can be in. One value, so two cannot coexist.
 *
 * There is no `found`: the answer to a found ticket is a navigation, and by the
 * time it would be written this component is on its way out.
 *
 * `number` on `missing` is what the **server was asked**, kept so the sentence
 * names it even after somebody starts correcting a digit in the field above.
 */
type Answer =
  | { readonly kind: 'idle' }
  | { readonly kind: 'looking' }
  | { readonly kind: 'missing'; readonly number: string }
  | { readonly kind: 'failed'; readonly error: unknown };

export function TicketLookup() {
  const client = useApiClient();
  const navigate = useNavigate();

  const form = useForm({
    resolver: zodResolver(lookupSchema),
    mode: 'onTouched',
    defaultValues: { number: '' },
  });

  const [answer, setAnswer] = useState<Answer>({ kind: 'idle' });

  const pending = form.formState.isSubmitting;

  const handleSubmit = async (values: z.infer<typeof lookupSchema>) => {
    // What was typed, trimmed by the schema and otherwise untouched. Not
    // uppercased and not stripped of its leading zeros: the API uppercases the
    // prefix itself and reads padding as presentation, so `t03-871` and
    // `T03-000871` are the same lookup — and the value that is sent is the value
    // somebody can see they typed.
    const { number } = values;

    setAnswer({ kind: 'looking' });

    try {
      const ticket = await client.request(TicketsController_readByNumber_v1, {
        path: { number },

        // `sequenceKind` is deliberately not sent. It disambiguates a park using
        // both of a terminal's counters — `T03-000871` off the TICKET counter
        // and off the RECEIPT counter are two artefacts the printed form spells
        // identically — and the API defaults it to `TICKET`, which is what a
        // visitor at a counter is holding. Nothing in this app issues against
        // the receipt counter today, so a control for it here would be a
        // question in the way of the answer. If receipts are ever printed, this
        // is where a second attempt would go.
      });

      // `replace: false`: the tickets list stays behind this in the history, so
      // Back returns to the day somebody was reading rather than to an empty
      // field.
      await navigate(ticketPath(ticket.id));
    } catch (error: unknown) {
      // Branched on the code and never on the status. A 404 here is an *answer*
      // to the question rather than a failure to answer it, and
      // `messageForFailure` would word it as "that record no longer exists,
      // go back and refresh the list" — which is about a row that was deleted
      // and is not what happened.
      if (isNotFound(error)) {
        setAnswer({ kind: 'missing', number });

        return;
      }

      // `VALIDATION_FAILED` means the server read the shape differently from the
      // field, which the rules in `ticket-number.ts` are written to prevent —
      // they are that parser restated. If it happens anyway the two have drifted,
      // and the honest place for the message is under the field that produced it
      // rather than in a panel about the lookup. Said in this app's own words
      // and not the server's prose: `docs/ui-conventions.md` is explicit that a
      // refusal is worded from its `code` and only from its `code`.
      //
      // The issue for this ticket says the validation message may be surfaced
      // directly. It is not, and that is the one place this screen departs from
      // it — noted in the PR.
      if (error instanceof ApiError && error.code === 'VALIDATION_FAILED') {
        form.setError('number', { message: TICKET_NUMBER_MALFORMED });
        setAnswer({ kind: 'idle' });

        return;
      }

      setAnswer({ kind: 'failed', error });
    }
  };

  return (
    <section
      aria-label="Find a ticket by its printed number"
      // A band of the field, not a card on it. The panel is the surface now, so
      // the bordered, rounded enamel box this used to draw was a card inside a
      // card — and with the field uncapped the one input in it stretched the
      // width of the panel. What is left is what a band is: the head's own
      // gutter, a measure for the field, and a rule between this and the records
      // under it. The negative margin is `ScreenTable`'s, for the same reason —
      // a rule reads as a band's edge only if it runs the full width of the
      // field.
      className="-mx-4 border-b border-border px-4 pb-4 lg:-mx-7 lg:px-7"
    >
      <div className="max-w-xl">
        <Form {...form}>
          <form noValidate onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}>
            <FormField
              control={form.control}
              name="number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required className={fieldLabel}>
                    Ticket number
                  </FormLabel>

                  {/* The button is **inside the field**, on the input's own row,
                      and the description sets under both. It used to be the
                      form's second flex child, bottom-aligned against a
                      description that was one line because the input was the
                      width of the card. On a measure that description is three
                      lines and the button floated level with the middle of it.
                      Nothing about the form changes — `FormControl` still wraps
                      the input, so the id, the description and the message are
                      still wired by the component. */}
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                    <FormControl
                      render={
                        <Input
                          // A number read aloud off an artefact and typed back
                          // in, so it gets the mono face and tabular figures —
                          // the same treatment the column in the list gives it,
                          // so the two look like the same fact.
                          className={`${fieldSurface} h-11 font-mono numeric sm:flex-1`}
                          placeholder={TICKET_NUMBER_EXAMPLE}
                          // Nothing here is a name, an address or a password,
                          // and a browser offering last week's numbers over the
                          // field is in the way of somebody reading this one off
                          // paper.
                          autoComplete="off"
                          spellCheck={false}
                          required
                          {...field}
                        />
                      }
                    />

                    {/* The screen's one accent. Nothing else on the tickets list
                        or on a ticket's record writes or navigates, so there is
                        no second control this could be confused with — and this
                        is the one somebody with a visitor at the counter is
                        looking for. */}
                    <Button type="submit" className="h-11 sm:shrink-0" disabled={pending}>
                      <IconSearch data-icon="inline-start" />
                      {pending ? 'Looking…' : 'Find'}
                    </Button>
                  </div>

                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        {/* `polite`: the answer arrives beside a field somebody has just used and
          is announced without cutting across what they are doing.

          The region is **always in the DOM**, empty while there is nothing to
          say. A live region that mounts at the same moment as its content is not
          reliably announced — the browser has nothing to compare against — and
          the first answer is the one somebody is waiting for. */}
        <div
          aria-live="polite"
          className={answer.kind === 'idle' ? undefined : 'mt-5 border-t border-border pt-4'}
        >
          <AnswerBody answer={answer} />
        </div>
      </div>
    </section>
  );
}

function AnswerBody({ answer }: { answer: Answer }) {
  switch (answer.kind) {
    case 'idle':
      return null;

    case 'looking':
      return <p className="text-sm text-muted-foreground">Looking…</p>;

    case 'missing':
      return (
        <div className="flex items-start gap-2.5">
          {/* Not `destructive` and not a warning triangle. Nothing went wrong:
              the question was asked and the answer was no. Red in this system
              means the system refused something or is about to. */}
          <IconTicketOff className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">No ticket with that number</p>
            <p className="text-sm text-muted-foreground">
              Nothing carries{' '}
              <span className="font-mono text-foreground numeric">{answer.number}</span>. Check the
              number on the ticket.
            </p>
            {/* The misreading this exists to prevent. On the one screen where
                somebody is asking about a charge that may have been refunded,
                an empty answer invites "so it must have been voided" — and a
                voided ticket answers here, marked. */}
            <p className="text-sm text-muted-foreground">Voided tickets are still found.</p>
          </div>
        </div>
      );

    case 'failed':
      return (
        <div className="flex items-start gap-2.5">
          <IconAlertTriangle className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">That lookup did not answer</p>
            {/* The code becomes words in one place for the whole app. A screen
                that worded this itself would re-separate refusals the API merges
                on purpose. */}
            <p className="text-sm text-muted-foreground">{messageForFailure(answer.error)}</p>
          </div>
        </div>
      );
  }
}
